/**
 * FollowUpWidget — Dashboard widget showing contacts that need follow-up.
 * Shows top 5 suggestions with warmth badge, reason, and quick actions.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageSquare, Clock, X, ChevronRight } from 'lucide-react';
import Panel from './common/Panel';
import api from '../services/api';

interface FollowUpSuggestion {
  contact_id: string;
  name: string;
  company: string | null;
  title: string | null;
  warmth: number;
  reason: string;
  urgency: 'high' | 'medium' | 'low';
  last_interaction: string | null;
  days_since_interaction: number | null;
  suggested_action: string;
  message_template: string | null;
  contact_url: string | null;
}

const REASON_LABELS: Record<string, string> = {
  cooling_off:          'Cooling off',
  high_value_untouched: 'Never contacted',
  recently_connected:   'New connection',
  pipeline_company:     'Pipeline match',
  meeting_no_followup:  'Meeting — no follow-up',
  unanswered_email:     'Unanswered email',
};

const URGENCY_COLORS: Record<string, string> = {
  high:   '#ef4444',
  medium: '#f59e0b',
  low:    '#3b82f6',
};

function warmthColor(warmth: number): string {
  if (warmth > 60) return '#ef4444';
  if (warmth > 40) return '#f59e0b';
  if (warmth > 20) return '#22c55e';
  return '#6b7280';
}

export default function FollowUpWidget() {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<FollowUpSuggestion[]>([]);
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snoozedIds, setSnoozedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [suggestRes, statsRes] = await Promise.allSettled([
        api.get('/reminders/follow-ups', { params: { limit: 5 } }),
        api.get('/reminders/stats'),
      ]);
      if (suggestRes.status === 'fulfilled') {
        setSuggestions(suggestRes.value.data || []);
      }
      if (statsRes.status === 'fulfilled') {
        setTotalPending(statsRes.value.data?.total_pending ?? 0);
      }
    } catch {
      // silently degrade
    } finally {
      setLoading(false);
    }
  };

  const handleSnooze = async (contactId: string) => {
    try {
      await api.post(`/reminders/snooze/${contactId}`, { days: 7 });
      setSnoozedIds(prev => new Set([...prev, contactId]));
    } catch {
      // best-effort
    }
  };

  const handleDismiss = async (contactId: string) => {
    try {
      await api.post(`/reminders/dismiss/${contactId}`);
      setSnoozedIds(prev => new Set([...prev, contactId]));
    } catch {
      // best-effort
    }
  };

  const handleMessage = (suggestion: FollowUpSuggestion) => {
    if (suggestion.contact_url) {
      window.open(suggestion.contact_url, '_blank', 'noopener,noreferrer');
    }
  };

  const visible = suggestions.filter(s => !snoozedIds.has(s.contact_id));

  const headerAction = (
    <button
      onClick={() => navigate('/reminders')}
      className="flex items-center gap-1 text-xs"
      style={{ color: 'var(--netrun-green)', background: 'none', border: 'none', cursor: 'pointer' }}
    >
      View all
      <ChevronRight size={12} />
    </button>
  );

  return (
    <Panel
      title={`Follow-ups${totalPending > 0 ? ` (${totalPending})` : ''}`}
      action={headerAction}
      noPadding
    >
      {loading && (
        <div className="px-5 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
          Loading...
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div className="px-5 py-8 text-center">
          <Bell size={24} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            You're all caught up — no follow-ups needed.
          </p>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
          {visible.map(s => (
            <div key={s.contact_id} className="px-5 py-3.5">
              {/* Row 1: Name + warmth badge */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {s.name}
                    </span>
                    {/* Warmth badge */}
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                      style={{
                        background: `${warmthColor(s.warmth)}18`,
                        color: warmthColor(s.warmth),
                        border: `1px solid ${warmthColor(s.warmth)}30`,
                      }}
                    >
                      {s.warmth}
                    </span>
                    {/* Reason badge */}
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{
                        background: `${URGENCY_COLORS[s.urgency]}15`,
                        color: URGENCY_COLORS[s.urgency],
                      }}
                    >
                      {REASON_LABELS[s.reason] || s.reason}
                    </span>
                  </div>
                  {(s.title || s.company) && (
                    <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-muted)' }}>
                      {[s.title, s.company].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  <p className="text-xs mt-1 truncate" style={{ color: 'var(--text-secondary)' }}>
                    {s.suggested_action}
                  </p>
                </div>
              </div>

              {/* Row 2: Action buttons */}
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => handleMessage(s)}
                  disabled={!s.contact_url}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-all"
                  style={{
                    background: 'rgba(144,185,171,0.1)',
                    border: '1px solid rgba(144,185,171,0.2)',
                    color: 'var(--netrun-green)',
                    cursor: s.contact_url ? 'pointer' : 'default',
                    opacity: s.contact_url ? 1 : 0.5,
                  }}
                >
                  <MessageSquare size={11} />
                  Message
                </button>
                <button
                  onClick={() => handleSnooze(s.contact_id)}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  <Clock size={11} />
                  Snooze 7d
                </button>
                <button
                  onClick={() => handleDismiss(s.contact_id)}
                  className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                  title="Dismiss"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
